import { useEffect, useMemo, useState } from "react";
import { ArrowRightLeft, Search } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { formatCurrency, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import type { CustoUnitarioCCStat } from "./chartUtils";
import {
  aggregateInsumoOrcamentoPorObra,
  mergeInsumoOrcamentoComparativo,
  type InsumoOrcamentoComparativoRow,
} from "./gerencialOrcamentoComparativo";
import {
  fetchBudgetResources,
  fetchAppropriatedResources,
} from "@/services/orcamento";

interface Props {
  open: boolean;
  onClose: () => void;
  ccStats: CustoUnitarioCCStat[];
  startDate: string;
  endDate: string;
  periodLabel?: string;
}

function defaultPairIds(stats: CustoUnitarioCCStat[]): { a: string; b: string } {
  if (stats.length < 2) return { a: "", b: "" };
  const sorted = [...stats].sort((x, y) => {
    const d = y.custoUnitario - x.custoUnitario;
    if (d !== 0) return d;
    return x.name.localeCompare(y.name, "pt-BR");
  });
  return {
    a: sorted[0]!.costCenterId,
    b: sorted[sorted.length - 1]!.costCenterId,
  };
}

function abbrevName(s: string, max = 32) {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

function fmtQty(unit: string, q: number): string {
  if (q <= 0) return "—";
  const n = formatNumber(q, 3);
  const u = (unit ?? "").trim();
  return u ? `${n} ${u}` : n;
}

function fmtPrecoUnit(p: number | null): string {
  if (p == null || !Number.isFinite(p) || p <= 0) return "—";
  return formatCurrency(p);
}

export function ComparativoInsumosModal({
  open,
  onClose,
  ccStats,
  startDate,
  endDate,
  periodLabel,
}: Props) {
  const [ccIdA, setCcIdA] = useState("");
  const [ccIdB, setCcIdB] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<InsumoOrcamentoComparativoRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || ccStats.length < 2) return;
    const { a, b } = defaultPairIds(ccStats);
    setCcIdA(a);
    setCcIdB(b);
    setQuery("");
  }, [open, ccStats]);

  const rowA = useMemo(
    () => ccStats.find((c) => String(c.costCenterId).trim() === String(ccIdA).trim()),
    [ccStats, ccIdA],
  );
  const rowB = useMemo(
    () => ccStats.find((c) => String(c.costCenterId).trim() === String(ccIdB).trim()),
    [ccStats, ccIdB],
  );

  useEffect(() => {
    if (!open) {
      setLoading(false);
      return;
    }
    if (!rowA || !rowB || rowA.costCenterId === rowB.costCenterId) {
      setRows([]);
      setError(null);
      setLoading(false);
      return;
    }
    const bidA = String(rowA.costCenterId).trim();
    const bidB = String(rowB.costCenterId).trim();
    const sd = (startDate ?? "").trim();
    const ed = (endDate ?? "").trim();
    if (!sd || !ed) {
      setRows([]);
      setError("Informe período válido nos filtros do Gerencial.");
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [bA, aA, bB, aB] = await Promise.all([
          fetchBudgetResources(bidA),
          fetchAppropriatedResources(bidA, sd, ed, { includeZeroValue: true }),
          fetchBudgetResources(bidB),
          fetchAppropriatedResources(bidB, sd, ed, { includeZeroValue: true }),
        ]);
        if (!alive) return;
        const aggA = aggregateInsumoOrcamentoPorObra(bA, aA);
        const aggB = aggregateInsumoOrcamentoPorObra(bB, aB);
        const merged = mergeInsumoOrcamentoComparativo(
          aggA,
          aggB,
          rowA.unitCount,
          rowB.unitCount,
        );
        setRows(merged);
      } catch (e) {
        if (!alive) return;
        setRows([]);
        setError(
          e instanceof Error
            ? e.message
            : "Não foi possível carregar orçamento/apropriações. Verifique se o ID da obra coincide com o do módulo Orçamento.",
        );
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [open, rowA, rowB, startDate, endDate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.label.toLowerCase().includes(q));
  }, [rows, query]);

  const sumRealizadoA = useMemo(() => rows.reduce((s, r) => s + r.realizadoA, 0), [rows]);
  const sumRealizadoB = useMemo(() => rows.reduce((s, r) => s + r.realizadoB, 0), [rows]);

  const topDrivers = useMemo(
    () =>
      [...rows]
        .filter((r) => r.absDiffRealizadoPerCasa > 0)
        .sort((a, b) => b.absDiffRealizadoPerCasa - a.absDiffRealizadoPerCasa)
        .slice(0, 5),
    [rows],
  );

  const topDriverKeys = useMemo(() => new Set(topDrivers.map((r) => r.key)), [topDrivers]);

  const metrics = useMemo(() => {
    let both = 0;
    let onlyA = 0;
    let onlyB = 0;
    for (const r of rows) {
      const aAct = r.qtdOrcadaA > 0 || r.qtdRealizadaA > 0 || r.realizadoA > 0;
      const bAct = r.qtdOrcadaB > 0 || r.qtdRealizadaB > 0 || r.realizadoB > 0;
      if (aAct && bAct) {
        both += 1;
      } else if (aAct) onlyA += 1;
      else if (bAct) onlyB += 1;
    }
    const deltaCustoUnitario =
      rowA && rowB ? rowA.custoUnitario - rowB.custoUnitario : 0;
    return { both, onlyA, onlyB, deltaCustoUnitario };
  }, [rows, rowA, rowB]);

  if (ccStats.length < 2) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2 min-w-0">
          <ArrowRightLeft className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          <span className="truncate">Comparativo de insumos entre obras</span>
        </span>
      }
      size="full"
    >
      <div className="flex flex-col gap-5 w-full">
        {periodLabel ? (
          <p className="text-xs text-slate-500 -mt-1">{periodLabel}</p>
        ) : null}

        <div className="flex flex-col xl:flex-row xl:items-end gap-4 flex-wrap">
          <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0">
            <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 min-w-0 flex-1">
              Obra A (referência «mais cara» no padrão)
              <select
                value={ccIdA}
                onChange={(e) => setCcIdA(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {ccStats.map((c) => (
                  <option key={c.costCenterId} value={c.costCenterId} disabled={c.costCenterId === ccIdB}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-xs font-semibold text-slate-600 min-w-0 flex-1">
              Obra B
              <select
                value={ccIdB}
                onChange={(e) => setCcIdB(e.target.value)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                {ccStats.map((c) => (
                  <option key={c.costCenterId} value={c.costCenterId} disabled={c.costCenterId === ccIdA}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="relative w-full xl:w-72 xl:flex-shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filtrar insumo…"
              className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
        </div>

        {rowA && rowB && rowA.costCenterId === rowB.costCenterId && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            Selecione duas obras distintas para comparar.
          </p>
        )}

        {rowA && rowB && rowA.costCenterId !== rowB.costCenterId && (
          <>
            {loading && (
              <div className="flex items-center justify-center py-8">
                <Loader size="md" label="Carregando orçamento e apropriações do Sienge…" />
              </div>
            )}

            {error && !loading && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                {error}
              </div>
            )}

            {!loading && !error && rows.length === 0 && (
              <p className="text-sm text-slate-500 py-4">
                Nenhum insumo com linha de orçamento ou apropriação encontrada para estas obras no período.
              </p>
            )}

            {!loading && !error && rows.length > 0 && (
              <>
                {/* KPIs — mesma faixa da referência */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate">
                      Custo unit. A
                    </p>
                    <p className="text-base sm:text-lg font-bold text-slate-800 tabular-nums mt-1">
                      {rowA.unitCount > 0 ? formatCurrency(rowA.custoUnitario) : "—"}
                    </p>
                    <p className="text-[10px] text-slate-500 truncate mt-1.5" title={rowA.name}>
                      {abbrevName(rowA.name)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 truncate">
                      Custo unit. B
                    </p>
                    <p className="text-base sm:text-lg font-bold text-slate-800 tabular-nums mt-1">
                      {rowB.unitCount > 0 ? formatCurrency(rowB.custoUnitario) : "—"}
                    </p>
                    <p className="text-[10px] text-slate-500 truncate mt-1.5" title={rowB.name}>
                      {abbrevName(rowB.name)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                      Δ Custo unitário
                    </p>
                    <p
                      className={cn(
                        "text-base sm:text-lg font-bold tabular-nums mt-1",
                        metrics.deltaCustoUnitario > 0
                          ? "text-orange-600"
                          : metrics.deltaCustoUnitario < 0
                            ? "text-emerald-700"
                            : "text-slate-800",
                      )}
                    >
                      {metrics.deltaCustoUnitario > 0 ? "+" : ""}
                      {formatCurrency(metrics.deltaCustoUnitario)}
                    </p>
                    <p className="text-[10px] text-emerald-900/85 mt-1.5 leading-tight">
                      A − B (R$/casa · parcelas)
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                      Insumos em ambas
                    </p>
                    <p className="text-xl font-bold text-slate-800 tabular-nums mt-1">
                      {formatNumber(metrics.both, 0)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-800">
                      Só obra A
                    </p>
                    <p className="text-xl font-bold text-rose-700 tabular-nums mt-1">
                      {formatNumber(metrics.onlyA, 0)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-sky-200 bg-sky-50/60 px-4 py-3 shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-sky-800">
                      Só obra B
                    </p>
                    <p className="text-xl font-bold text-sky-700 tabular-nums mt-1">
                      {formatNumber(metrics.onlyB, 0)}
                    </p>
                  </div>
                </div>

                {/* Top 5 dispersões — R$/casa realizado (orçamento) */}
                <div>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
                    Maiores dispersões em R$/casa no realizado (top 5)
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {topDrivers.length === 0 ? (
                      <p className="text-sm text-slate-400 col-span-full py-2">
                        Sem diferença de realizado por casa entre as obras.
                      </p>
                    ) : (
                      topDrivers.map((r, i) => (
                        <div
                          key={r.key}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                        >
                          <p className="text-[10px] font-bold text-slate-400">#{i + 1}</p>
                          <p
                            className="text-xs font-semibold text-slate-800 leading-snug line-clamp-2 mt-1 min-h-[2.25rem]"
                            title={r.label}
                          >
                            {r.label}
                          </p>
                          <p className="text-sm font-bold text-orange-600 tabular-nums mt-2">
                            ±{formatCurrency(r.absDiffRealizadoPerCasa)}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-1">
                            {r.diffRealizadoPerCasa > 0
                              ? "Obra A mais alta"
                              : r.diffRealizadoPerCasa < 0
                                ? "Obra B mais alta"
                                : "—"}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Tabela — apenas colunas combinadas (realizado + gap + %) */}
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="overflow-x-auto max-h-[min(52vh,560px)] overflow-y-auto">
                    <table className="w-full text-xs sm:text-sm min-w-[900px]">
                      <thead className="sticky top-0 z-10 bg-slate-100 border-b border-slate-200">
                        <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                          <th className="px-3 py-3 w-[26%]">Insumo</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">Qtd realiz. A</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">Qtd realiz. B</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">P.U. realiz. A</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">P.U. realiz. B</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">Gap %</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">% realiz. A</th>
                          <th className="px-3 py-3 text-right whitespace-nowrap">% realiz. B</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filtered.map((r, idx) => (
                          <tr
                            key={r.key}
                            className={cn(
                              idx % 2 === 1 ? "bg-slate-50/40" : "bg-white",
                              "hover:bg-amber-50/30 transition-colors",
                              topDriverKeys.has(r.key) ? "bg-orange-50/50" : "",
                            )}
                          >
                            <td className="px-3 py-2.5 align-top">
                              <span className="font-semibold text-slate-800 text-[13px] leading-snug">
                                {r.label}
                              </span>
                              {r.unit ? (
                                <p className="text-[10px] text-slate-500 mt-0.5">UM: {r.unit}</p>
                              ) : null}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">
                              {fmtQty(r.unit, r.qtdRealizadaA)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">
                              {fmtQty(r.unit, r.qtdRealizadaB)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-800 whitespace-nowrap">
                              {fmtPrecoUnit(r.precoRealizadoA)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-800 whitespace-nowrap">
                              {fmtPrecoUnit(r.precoRealizadoB)}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">
                              {r.relSpreadPct == null ? "—" : `${formatNumber(r.relSpreadPct, 1)}%`}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                              {sumRealizadoA > 0 && r.realizadoA > 0
                                ? `${formatNumber((r.realizadoA / sumRealizadoA) * 100, 1)}%`
                                : "—"}
                            </td>
                            <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 whitespace-nowrap">
                              {sumRealizadoB > 0 && r.realizadoB > 0
                                ? `${formatNumber((r.realizadoB / sumRealizadoB) * 100, 1)}%`
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-slate-500 px-4 py-2.5 bg-slate-50 border-t border-slate-100">
                    {filtered.length} insumo{filtered.length !== 1 ? "s" : ""}
                    {query.trim() ? ` · filtrado de ${rows.length}` : ""}
                    {" · "}
                    Ordenação: maior |Δ R$/casa| no realizado (orçamento).
                  </p>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
